/* Flight facts as S3 rows and the S5 board show them (the prototype's FLAP_BY_STATUS,
   delayCaption, humanMins). Shared by S3 and S5. */
import type { FlapTone } from '../../components';
import type { T } from '../../i18n';
import type { DisplayStatus, Flight } from '../../lib/api';

/** Status flap text and tone per `displayStatus` (the API computes it; eligibility never reads it). */
export const FLAP_BY_STATUS: Record<DisplayStatus, { key: `S3.flap.${DisplayStatus}`; tone: FlapTone }> = {
  cancelled: { key: 'S3.flap.cancelled', tone: 'red' },
  delayed: { key: 'S3.flap.delayed', tone: 'amber' },
  on_time: { key: 'S3.flap.on_time', tone: 'green' },
  diverted: { key: 'S3.flap.diverted', tone: 'red' },
  pending: { key: 'S3.flap.pending', tone: 'dim' },
};

/**
 * The flap for a flight. Contract 1.4: a `landed` flight (touchdown, no gate time) has no
 * `displayStatus` of its own; it is DELAYED by the same line, and on time it reads LANDED,
 * never ARRIVED (we don't know when it reached the gate).
 */
export function flapOf(f: Flight | null | undefined): {
  key: `S3.flap.${DisplayStatus | 'landed'}`;
  tone: FlapTone;
} {
  if (f?.status === 'landed' && f.displayStatus === 'on_time')
    return { key: 'S3.flap.landed', tone: 'green' };
  return FLAP_BY_STATUS[f?.displayStatus ?? 'pending'];
}

/** A shown time came from Flightradar24, whose terms ask for a credit under it (ToS 8.2, contract 1.4). */
export const creditsFr24 = (f: Flight | null | undefined): boolean =>
  !!f?.attribution?.includes('flightradar24');

/** 200 → "3 h 20 min", 14 → "14 min", 180 → "3 h". */
export function humanMins(t: T, m: number): string {
  if (m < 60) return t('S3.mins', { m });
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? t('S3.hoursMins', { h, m: r }) : t('S3.hours', { h });
}

/** The line under a row's flap: "arrived 3 h 20 min late", "landed 2 h 41 min late", "never arrived", "details pending". */
export function delayCaption(t: T, f: Flight): string {
  switch (f.displayStatus) {
    case 'cancelled':
      return t('S3.caption.cancelled');
    case 'diverted':
      return t('S3.caption.diverted');
    case 'pending':
      return t('S3.caption.pending');
    default:
      if (f.arrivalDelayMin === null) return '';
      /* 1.4: a touchdown is shown as a landing, never as an arrival at the gate. */
      if (f.status === 'landed')
        return f.arrivalDelayMin <= 0
          ? t('S3.caption.landedOnTime')
          : t('S3.caption.landedLate', { delay: humanMins(t, f.arrivalDelayMin) });
      if (f.arrivalDelayMin <= 0) return t('S3.caption.onTime');
      return t('S3.caption.late', { delay: humanMins(t, f.arrivalDelayMin) });
  }
}

/** 'YYYY-MM-DDTHH:MM' → 'HH:MM'. */
export const hhmm = (local: string | null | undefined) => (local ? local.slice(11, 16) : '—');

/** What the S3 filter matches: operating and marketing idents and the carrier (rule 6, architecture.md §4.2). */
export function matchesFilter(f: Flight, q: string): boolean {
  const n = (s: string) => s.toLowerCase().replace(/\s+/g, '');
  const needle = n(q);
  if (!needle) return true;
  return [f.operatingIdent, f.operator.name, f.operator.iata ?? '', ...f.marketingIdents].some((v) =>
    n(v).includes(needle),
  );
}

/** 'YYYY-MM-DD' + n days (local calendar arithmetic, no time zones). */
export function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d! + n));
  return dt.toISOString().slice(0, 10);
}
